import { create } from "zustand";
import { storage } from "../lib/storage";

/**
 * Whether the first-visit intro (GRA-137) has been dismissed. Persisted like
 * the settings and account stores: read once at module init, written back on
 * every change, so a returning player never sees it again.
 */

const DISMISSED_KEY = "intro:dismissed";

/**
 * `?intro=off` skips the intro for this load without persisting anything, so
 * an embed (the Grasshopper Labs portfolio iframe) opens straight on a chart.
 */
function skippedByQuery(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("intro") === "off";
}

export interface IntroState {
  dismissed: boolean;
  dismiss: () => void;
  /**
   * Put the intro back on screen. The persisted flag is left alone:
   * a player who reopens the intro and closes it again is dismissing it
   * afresh, and `dismiss` writes the flag back either way, so nothing here
   * needs to clear it.
   */
  show: () => void;
}

export const useIntroStore = create<IntroState>((set) => ({
  dismissed: skippedByQuery() || storage.get<boolean>(DISMISSED_KEY, false),
  dismiss: () => {
    storage.set(DISMISSED_KEY, true);
    set({ dismissed: true });
  },
  show: () => set({ dismissed: false }),
}));
